import { mapActions, mapGetters } from 'vuex';

export default {
  methods: {
    ...mapActions('Creative', ['ActionSetCreativeLogs', 'ActionSetFilterCreativeLog']),

    buildCreativeLogQuery(page, perPage) {
      let query = '';
      if (this.filterCreativeLog) query = `${this.filterCreativeLog}`;
      query += (query ? '%26' : '') + 'orderBy=created_at|desc';
      if (!page) query += '%26page=1';
      else query += `%26${page}`;
      if (!perPage) query += `%26perPage=${localStorage.getItem('rowsPerPage') || 10}`;
      else query += perPage;
      return query;
    },

    async enrichCreativeLogs(logs) {
      const userIds = [...new Set((logs || []).map((item) => item.user_id).filter(Boolean))];
      if (!userIds.length) {
        return (logs || []).map((item) => ({
          ...item,
          user_name: item.user_name || '-',
          user_email: item.user_email || '-'
        }));
      }

      try {
        const { data, status } = await this.$http.get(`/user/get-group-by-id/${userIds.join(',')}`);
        if (status !== 200 || !Array.isArray(data)) {
          return logs;
        }

        const userMap = data.reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {});

        return logs.map((item) => ({
          ...item,
          user_name: (userMap[item.user_id] && userMap[item.user_id].name) || '-',
          user_email: (userMap[item.user_id] && userMap[item.user_id].email) || '-'
        }));
      } catch (error) {
        return (logs || []).map((item) => ({
          ...item,
          user_name: item.user_name || '-',
          user_email: item.user_email || '-'
        }));
      }
    },

    async getCreativeLogs(page, perPage) {
      const query = this.buildCreativeLogQuery(page, perPage);
      this.onLoading(true);
      try {
        const { data, status } = await this.$http.get(
          '/redirect?app=CREATIVE&path=/api/get/creative/log?' + query
        );
        if (status === 200) {
          const enriched = await this.enrichCreativeLogs(data.data || []);
          this.ActionSetCreativeLogs(enriched);
          this.ActionSetPagination(data);
        }
      } catch (error) {
        if (error.response && error.response.status === 500)
          this.errorNotify(this.$t('problem_try_again_later'));
        else this.errorNotify(error);
      } finally {
        this.onLoading(false);
      }
    },

    async getAllCreativeLogs() {
      const baseFilter = this.filterCreativeLog || '';
      let page = 1;
      let lastPage = 1;
      const allRows = [];

      this.onLoading(true);
      try {
        do {
          let query = baseFilter;
          query += (query ? '%26' : '') + 'orderBy=created_at|desc';
          query += `%26page=${page}%26perPage=100`;

          const { data, status } = await this.$http.get(
            '/redirect?app=CREATIVE&path=/api/get/creative/log?' + query
          );

          if (status !== 200) break;

          const enriched = await this.enrichCreativeLogs(data.data || []);
          allRows.push(...enriched);
          lastPage = data.last_page || 1;
          page += 1;
        } while (page <= lastPage);

        return allRows;
      } catch (error) {
        if (error.response && error.response.status === 500)
          this.errorNotify(this.$t('problem_try_again_later'));
        else this.errorNotify(error);
        return [];
      } finally {
        this.onLoading(false);
      }
    }
  },

  computed: {
    ...mapGetters('Creative', ['creativeLogs', 'filterCreativeLog']),

    creativeLogColumns() {
      return [
        {
          name: 'user_id',
          label: this.$t('user_id'),
          field: 'user_id',
          align: 'left',
          sortable: true
        },
        {
          name: 'user_name',
          label: this.$t('user_name'),
          field: 'user_name',
          align: 'left',
          sortable: true
        },
        {
          name: 'user_email',
          label: this.$t('email'),
          field: 'user_email',
          align: 'left',
          sortable: true
        },
        {
          name: 'creative_id',
          label: this.$t('creative_id'),
          field: 'creative_id',
          align: 'left',
          sortable: true
        },
        {
          name: 'creative_name',
          label: this.$t('creative_name'),
          field: 'creative_name',
          align: 'left',
          sortable: true
        },
        {
          name: 'functionality',
          label: this.$t('functionality'),
          field: 'functionality',
          align: 'left'
        },
        {
          name: 'operation',
          label: this.$t('operation'),
          field: 'operation',
          align: 'left'
        },
        {
          name: 'origin',
          label: this.$t('origin'),
          field: 'origin',
          align: 'left'
        },
        {
          name: 'ip_address',
          label: this.$t('ip_address'),
          field: 'ip_address',
          align: 'left'
        },
        {
          name: 'geolocation',
          label: this.$t('geolocation'),
          field: 'geolocation',
          align: 'left'
        },
        {
          name: 'user_agent',
          label: this.$t('user_agent'),
          field: 'user_agent',
          align: 'left'
        },
        {
          name: 'request_id',
          label: this.$t('request_id'),
          field: 'request_id',
          align: 'left'
        },
        {
          name: 'old_value',
          label: this.$t('previous_value'),
          field: 'old_value',
          align: 'left'
        },
        {
          name: 'new_value',
          label: this.$t('current_value'),
          field: 'new_value',
          align: 'left'
        },
        {
          name: 'created_at',
          label: this.$t('change_date'),
          field: 'created_at',
          align: 'left',
          sortable: true,
          format: (val) => (val ? this.$moment(val).format('L LTS') : '-')
        }
      ];
    }
  }
};
